import { Grid2 } from "@mui/material";
import { useEffect, useMemo } from "react";
import useTrees from "../../../Hooks/useTrees";
import useUIBp from "../../../Hooks/useUIBoilerplate";
import useURLQuery from "../../../Hooks/useURLQuery";
import { useWorker } from "../../../Hooks/useWorker";
import { UIactions } from "../../../store/slices/UI";
import { treesActions } from "../../../store/slices/trees";
import { warehouseActions } from "../../../store/slices/warehouse";
import { workerActions } from "../../../store/slices/workers";
import { sortObjByKey } from "../../../utility/misc/sortObjByKey";
import { currentCatsUI } from "../../Dashboard/Components/Drawer/Components/MailCategories/MailCategories";
import { defaultPagesCount } from "../../UI/GeneralPurpose/Pagination";
import SlimAlert from "../../UI/GeneralPurpose/SlimAlert";
import {
  defaultFilter,
  defaultPagination,
  defaultSelected,
  defaultSort,
} from "../../UI/GeneralPurpose/genericIDs";
import getMailCategories, {
  getMailCategoriesId,
} from "../Actions/getMailCategories";
import getMails, { getMailsId, mailsData, mailsTotalId } from "../Actions/getMails";
import Core from "../Components/Core";

const UIText = {
  "es-CL":
    "El usuario nunca ha habilitado y respaldado correos utilizando Windows365",
  "en-US": "The user has never enabled and backed up emails using Windows365",
};

const InboxLocaleArray = ["bandeja de entrada"];

const Mails = () => {
  const { auth, dispatch, navigate, warehouse, UI, settings } = useUIBp();
  const trees = useTrees();
  const { folderId, idx } = useURLQuery();
  const mailsTotal = UI[mailsTotalId] ?? 0;
  const workerId1 = getMailsId;
  const workerId2 = getMailCategoriesId;
  const { done: d1, working: w1, error: e1 } = useWorker(workerId1);
  const { done: d2, working: w2, error: e2 } = useWorker(workerId2);
  const working = w1 || w2;
  const done = d1 && d2;
  const error = !!e1 ? e1 : e2;
  const workerId = d2 && !e2 ? workerId1 : workerId2;

  const info = UIText[settings.locale];

  const filter = UI.filter ?? "";
  const sort = UI.sort ?? {};
  const pagination = UI.pagination ?? { page: 0, count: 0, rowsPerPage: 0 };

  const query = useMemo(() => {
    let baseQuery = "?";
    // ?searchInput=from:{john@example.com}to:{jane@example.com}subject:{Meeting}startDate:{2025-05-01}endDate:{2025-05-08}
    if (filter) {
      baseQuery = baseQuery+"searchInput=";
      const fromMatch = filter.match(/from:\(([^)]+)\)/);
      const fromValue = fromMatch ? fromMatch[1] : "";
      if (fromValue) {
        baseQuery += `from:{${fromValue}}`;
      }
      const toMatch = filter.match(/to:\(([^)]+)\)/);
      const toValue = toMatch ? toMatch[1] : "";
      if (toValue) {
        baseQuery += `to:{${toValue}}`;
      }
      const subjectMatch = filter.match(/subject:\(([^)]+)\)/);
      const subjectValue = subjectMatch ? subjectMatch[1] : "";
      if (subjectValue) {
        baseQuery += `subject:{${subjectValue}}`;
      }
      const startDateMatch = filter.match(/\bstartDate:(\d+-\d+-\d+)\b/);
      const startDateValue = startDateMatch ? startDateMatch[1] : "";
      if (startDateValue) {
        let sDate = startDateValue.split("-");
        sDate = `${sDate[2]}-${sDate[1]}-${sDate[0]}`;
        baseQuery += `startDate:{${sDate}}`;
      }
      const endDateMatch = filter.match(/\bendDate:(\d+-\d+-\d+)\b/);
      const endDateValue = endDateMatch ? endDateMatch[1] : "";
      if (endDateValue) {
        let eDate = endDateValue.split("-");
        eDate = `${eDate[2]}-${eDate[1]}-${eDate[0]}`;
        baseQuery += `endDate:{${eDate}}`;
      }
      const inValue = filter
        .replace(/from:\([^)]+\)/g, "") // Remove from:(...)
        .replace(/to:\([^)]+\)/g, "") // Remove to:(...)
        .replace(/subject:\([^)]+\)/g, "") // Remove subject:(...)
        .replace(/\bstartDate:\d+-\d+-\d+\b/g, "") // Remove startDate:...
        .replace(/\bendDate:\d+-\d+-\d+\b/g, "") // Remove endDate:...
        .trim(); // Trim any extra spaces
      if (inValue) {
        baseQuery += `in:{${inValue}}`;
      }
      baseQuery += `&limit=${defaultPagesCount}&offset=${defaultPagesCount*pagination.page}`;
    } else {
      baseQuery += `limit=${defaultPagesCount}&offset=${defaultPagesCount*pagination.page}`;
    }
    return baseQuery;

  }, [filter, pagination.page]);

  useEffect(() => {
    let found = false;
    let inboxId = "";
    for (const key in trees) {
      if (!key.match("mails-")) {
        continue;
      }
      const treeRoot = trees[key][0];
      if (treeRoot.id === folderId) {
        found = true;
      }
      if (
        !!InboxLocaleArray.find(
          (x) => x.toLowerCase() === treeRoot.displayName.toLowerCase()
        )
      ) {
        inboxId = treeRoot.id;
      }
    }
    if (!found) {
      navigate(`/dashboard/mails?folderId=${inboxId}&idx=0`);
    }
  }, [navigate, trees, folderId]);

  useEffect(() => {
    if (!d2) {
      dispatch(getMailCategories({ ...auth }));
    }
  }, [dispatch, auth, d2]);

  // The URL `folderId` is the tree ROOT id (used to look up the folder tree)
  // and `idx` points to the actual node inside that tree. Messages must be
  // fetched from the selected node's own id, not the root's, otherwise child
  // folders would show their root ancestor's mails.
  const messageFolderId = useMemo(() => {
    const tree = trees[`mails-${folderId}`] ?? [];
    const node = tree[Number(idx)] ?? {};
    return node.id ?? folderId;
  }, [trees, folderId, idx]);

  useEffect(() => {
    if (messageFolderId /*&& !d1*/) {
      console.log("query", query);
      const controller = new AbortController();
      dispatch(getMails({ ...auth, query, folderId: messageFolderId, controller }));
      return () => {
        controller.abort();
      };
    }
  }, [dispatch, query, messageFolderId, auth]);

  // filtering devices
  const filteredMails = useMemo(() => {
    const mails = warehouse[mailsData] ?? [];
    return (
      [...mails]
        // .filter(
        //   (m) =>
        //     lowerMatch(m.fromM, filter) ||
        //     lowerMatch(m.toRecipients, filter) ||
        //     lowerMatch(m.subject, filter) ||
        //     lowerMatch(m.bodyPreview, filter)
        // )
        .sort((a, b) => sortObjByKey(a, b, sort.value, sort.descending))
    );
  }, [sort.value, sort.descending, warehouse]);

  // Array to select all
  const allMails = useMemo(() => {
    return filteredMails.map((x) => x.id);
  }, [filteredMails]);

  // Initializing pagination
  useEffect(() => {
    dispatch(UIactions.setPagination({ count: mailsTotal }));
  }, [dispatch, mailsTotal]);

  // // Trimming devices to only current page ones
  // const { from, to } = CalculatePagination(pagination);
  // const paginatedMails = useMemo(() => {
  //   return filteredMails.filter((x, idx) => from <= idx + 1 && idx < to);
  // }, [from, to, filteredMails]);

  useEffect(() => {
    return () => {
      dispatch(UIactions.clear(defaultPagination));
      dispatch(UIactions.clear(defaultSort));
      dispatch(UIactions.clear(defaultFilter));
      dispatch(UIactions.clear(defaultSelected));
      dispatch(UIactions.clear(currentCatsUI));
      dispatch(treesActions.removeTreeGroup("mails-"));
      dispatch(warehouseActions.unload(mailsData));
      dispatch(workerActions.clear(getMailCategoriesId));
      dispatch(workerActions.clear(getMailsId));
      dispatch(workerActions.clearGroup("restore"));
    };
  }, [dispatch]);

  let foundCategories = false;
  for (const key in trees) {
    if (!key.match("mails-")) {
      continue;
    }
    foundCategories = true;
    break;
  }

  if (d2 && !e2 && !foundCategories) {
    return (
      <Grid2 container {...{ size: 12, justifyContent: "center", padding: 10 }}>
        <SlimAlert notCollapse {...{ severity: "info", errors: [info] }} />
      </Grid2>
    );
  }
  return (
    <Core
      {...{ mails: filteredMails, allMails, done, working, error, workerId }}
    />
  );
};
export default Mails;
